import { z } from 'zod';

const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_REGEX = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const addressSchema = z
  .object({
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip: z.string().min(1, 'ZIP code is required'),
    country: z.string().min(1, 'Country is required'),
    streetAddress: z.string().min(1, 'Street address is required'),
  })
  .superRefine((data, ctx) => {
    if (data.country === 'US' && data.zip && !US_ZIP_REGEX.test(data.zip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid ZIP code (e.g. 12345 or 12345-6789)',
        path: ['zip'],
      });
    }
    if (data.country === 'CA' && data.zip && !CA_POSTAL_REGEX.test(data.zip)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid postal code (e.g. A1B 2C3)', path: ['zip'] });
    }
    if (data.country === 'CA' && !data.state) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Province is required', path: ['state'] });
    }
  });

const optionalAddressSchema = z
  .object({
    city: z.string().optional().default(''),
    state: z.string().optional().default(''),
    zip: z.string().optional().default(''),
    country: z.string().min(1, 'Country is required'),
    streetAddress: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    if (data.country === 'US' && data.zip && !US_ZIP_REGEX.test(data.zip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid ZIP code (e.g. 12345 or 12345-6789)',
        path: ['zip'],
      });
    }
    if (data.country === 'CA' && data.zip && !CA_POSTAL_REGEX.test(data.zip)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid postal code (e.g. A1B 2C3)', path: ['zip'] });
    }
  });

export const customerFormSchema = z.object({
  billingAddress: addressSchema,
  deliveryAddress: optionalAddressSchema,
  sameAsBilling: z.boolean().default(false),
  name: z.string().min(1, 'Name is required'),
  phone: z
    .string()
    .min(1, 'Phone is required')
    .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Invalid phone format (e.g. (555) 123-4567)'),
  email: z.string().email('Invalid email (e.g. example@mail.com)').or(z.literal('')).optional(),
});

export type CustomerFormValues = z.infer<typeof customerFormSchema>;

const defaultAddress = {
  zip: '',
  city: '',
  state: '',
  country: 'US',
  streetAddress: '',
};

export const customerFormDefaultValues: CustomerFormValues = {
  name: '',
  phone: '',
  email: '',
  sameAsBilling: true,
  billingAddress: defaultAddress,
  deliveryAddress: defaultAddress,
};
